import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, connect } from 'react-redux';
import { Search } from 'carbon-components-react';
import ResultsBox from './resultsBox';
import {
  MAP_DATA_SERVICE_DATA_SET,
  CFCProps,
  MapGetPlaceSuggestPayload,
  MapSearchLocationHistoryObject,
  MapPlaceSuggestion,
  MapPlaceSuggestionCustomFields
} from 'types';
import Util from 'utilities/helper.util';
import { setMapSearchLocationHistory } from 'redux/app/actions';
import {
  getPlaceSuggestStart,
  clearPlaceSuggest,
  getGeoscapeAddressStart,
  getPlaceCandidateAddressStart,
  clearGeoscapeAddress,
  clearPlaceCandidateAddress
} from 'redux/map/actions';
import {
  clearECData,
  setECGeoJSONPoints
} from 'redux/emergencyContacts/actions';
import { addAlert } from 'redux/alerts/actions';
import { RootState } from 'redux/root.reducer';
import { useEventListener } from 'utilities/hooks.util';
import { optimizeLabel, constructArcGISWorldGeocodingLocality } from './helper';
import { removePinAndBuffer } from 'components/Map/helper';

const Searchpanel: React.FC<Props> = ({
  suggestion,
  error,
  user,
  mapSearchLocationHistory,
  placeHolder,
  defaultValue,
  geoscapeAddress,
  addressCandidate
}: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>(
    defaultValue || ''
  );
  const [isShowResult, setIsShowResult] = useState<Boolean>(false);
  const [
    userMapLocationSearchHistory,
    seUserMapLocationSearchHistory
  ] = useState<MapSearchLocationHistoryObject[]>([]);
  const dispatch = useDispatch();
  const debouncedFunction = useCallback(
    Util.debounce(async (args: string[]) => {
      const value = args[0];
      if (value.length < 3) {
        return;
      }
      const payload: MapGetPlaceSuggestPayload = {
        datasets: MAP_DATA_SERVICE_DATA_SET.GNAF,
        query: value,
        fields: 'region,locality',
        limit: '5'
      };

      if (window.loadedView && window.loadedView.center) {
        payload.location = `${window.loadedView.center.longitude},${window.loadedView.center.latitude}`;
      }

      dispatch(getPlaceSuggestStart(payload));
    }, 500),
    []
  );

  const saveHistory = useCallback(
    (item: MapPlaceSuggestion) => {
      const searchHistories = mapSearchLocationHistory || {};
      const { Value } = item;

      const searchHistoryOtherScope = userMapLocationSearchHistory.filter(
        (sh: any) => sh.scope !== MAP_DATA_SERVICE_DATA_SET.GNAF
      );
      let newSearchHistorySameScope = userMapLocationSearchHistory.filter(
        (sh: any) =>
          sh.Value !== Value && sh.scope === MAP_DATA_SERVICE_DATA_SET.GNAF
      );
      const newHistory = {
        scope: MAP_DATA_SERVICE_DATA_SET.GNAF,
        ...item
      };

      newSearchHistorySameScope = [newHistory, ...newSearchHistorySameScope];
      if (newSearchHistorySameScope.length > 5) {
        newSearchHistorySameScope.pop();
      }
      const newSearchHistory = [
        ...newSearchHistorySameScope,
        ...searchHistoryOtherScope
      ];
      searchHistories[user.id] = newSearchHistory;
      seUserMapLocationSearchHistory(newSearchHistory);
      dispatch(setMapSearchLocationHistory(searchHistories));
    },
    [userMapLocationSearchHistory, mapSearchLocationHistory, user.id, dispatch]
  );

  const processAddressCandidate = useCallback(() => {
    const addrCandidate = addressCandidate?.[0];

    if (addrCandidate) {
      const addrCandidateAttr = addrCandidate.attributes;
      const CustomFields: MapPlaceSuggestionCustomFields = {
        locality:
          addrCandidateAttr.Addr_type === 'Locality'
            ? `${constructArcGISWorldGeocodingLocality(addrCandidateAttr)}`
            : '',
        region: addrCandidate.attributes.Region || ''
      };
      const Coordinate: string = `${addrCandidate.location.x}, ${addrCandidate.location.y}`;

      dispatch(setECGeoJSONPoints(Coordinate));

      user?.id &&
        saveHistory({
          Coordinate,
          CustomFields,
          Highlight: addrCandidate.address,
          Score: addrCandidate.score.toString(),
          Value: addrCandidate.address,
          Extent: addrCandidate.extent
        });
      setSearchInputValue(
        optimizeLabel(addrCandidate.address, CustomFields, true, true)
      );
    } else {
      dispatch(
        addAlert(
          'map-location-search-alert',
          'error',
          'Error',
          'Invalid map search data'
        )
      );
    }

    dispatch(clearPlaceCandidateAddress());
  }, [addressCandidate, user, saveHistory, dispatch]);

  const processGeoscapeAddress = useCallback(() => {
    if (
      geoscapeAddress &&
      geoscapeAddress.coordinate &&
      geoscapeAddress.selectedSuggestion
    ) {
      dispatch(setECGeoJSONPoints(geoscapeAddress.coordinate));
      user?.id && saveHistory(geoscapeAddress.selectedSuggestion);
      setSearchInputValue(
        optimizeLabel(
          geoscapeAddress.selectedSuggestion.Value,
          geoscapeAddress.selectedSuggestion.CustomFields,
          false,
          false
        )
      );
    } else {
      dispatch(
        addAlert(
          'map-location-search-alert',
          'error',
          'Error',
          'Invalid map search data'
        )
      );
    }

    dispatch(clearGeoscapeAddress());
  }, [geoscapeAddress, user, saveHistory, dispatch]);

  useEffect(() => {
    setSearchInputValue(defaultValue || '');
  }, [defaultValue]);

  useEffect(() => {
    if (error) {
      dispatch(addAlert('map-location-search-alert', 'error', 'Error', error));
      dispatch(clearPlaceSuggest());
      dispatch(clearECData());
      removePinAndBuffer();
    }
  }, [error, dispatch]);

  useEffect(() => {
    user?.id &&
      mapSearchLocationHistory?.[user.id] &&
      seUserMapLocationSearchHistory(mapSearchLocationHistory[user.id]);
  }, [user, mapSearchLocationHistory]);

  useEffect(() => {
    if (addressCandidate) {
      processAddressCandidate();
    }
  }, [addressCandidate, processAddressCandidate]);

  useEffect(() => {
    if (geoscapeAddress) {
      processGeoscapeAddress();
    }
  }, [geoscapeAddress, processGeoscapeAddress]);

  const onClickResult = (item: any) => {
    if (item.AddressId) {
      dispatch(
        getGeoscapeAddressStart({
          selectedSuggestion: item
        })
      );
    } else if (item.Value && item.Coordinate && item.CustomFields) {
      dispatch(setECGeoJSONPoints(item?.Coordinate));
      user?.id && saveHistory(item);
      setSearchInputValue(optimizeLabel(item.Value, item.CustomFields));
    } else if (item.MagicKey && item.Value) {
      // This is for list coming from ArcGIS World Geocoding
      dispatch(
        getPlaceCandidateAddressStart({
          query: item.Value,
          outFields: '*',
          magicKey: item.MagicKey
        })
      );
    } else {
      dispatch(
        addAlert(
          'map-location-search-alert',
          'error',
          'Error',
          'Invalid map search data'
        )
      );
    }
    setIsShowResult(false);
  };

  const handleInputChange = (value: string) => {
    if (value) {
      debouncedFunction(value);
    } else {
      dispatch(clearPlaceSuggest());
      dispatch(clearECData());
      removePinAndBuffer();
    }
    setSearchInputValue(value);
  };

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (containerRef.current && evt.target) {
        if (!containerRef.current.contains(evt.target)) {
          setIsShowResult(false);
        }
      }
    },
    [containerRef]
  );

  const onClickCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (placeHolder) {
          dispatch(
            setECGeoJSONPoints(
              `${position.coords.longitude}, ${position.coords.latitude}`
            )
          );
        }
        setSearchInputValue('');
      },
      (err) => {
        const title = 'Error';
        const msg = err.message || err.toString();
        dispatch(addAlert('map-location-search-alert', 'error', title, msg));
      }
    );
    setIsShowResult(false);
  };

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  return (
    <div ref={containerRef} className={'emergency-contact-address-container'}>
      <div className="eca-search-container">
        <Search
          id="emergency-contact-address-input"
          labelText=""
          size="sm"
          value={searchInputValue}
          placeHolderText={placeHolder}
          onFocus={() => setIsShowResult(true)}
          onChange={(evt) => handleInputChange(evt.target.value)}
        />
      </div>
      {isShowResult && (
        <div className="mls-item-container">
          <ResultsBox
            userSearchHistory={userMapLocationSearchHistory}
            items={suggestion}
            onItemClick={onClickResult}
            onClickCurrentLocation={onClickCurrentLocation}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ app, mapCache, session }: RootState) => ({
  mapSearchLocationHistory: app.mapSearchLocationHistory,
  user: session.currentUser,
  suggestion: mapCache.suggestion,
  error: mapCache.suggestionError,
  geoscapeAddress: mapCache.geoscapeAddress,
  addressCandidate: mapCache.addressCandidate
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector> & any;

export default connector(Searchpanel);
